import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useFieldArray, useForm } from 'react-hook-form';
import { initialPersonalDataStepFormValues } from 'models/forms/applicationForm';
import { FormInput } from 'components/molecules/FormInput';
import { FormRadioGroup } from 'components/molecules/FormRadioGroup';
import ApplicationStore from 'state/ApplicationStore';
import { EDictionaryTypes } from 'models/dictionary';
import { FormSelect } from 'components/molecules/FormSelect';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { FormDatePicker } from 'components/molecules/FormDatePicker';
import { useApplicationDetails } from 'pages/MyApplicationsPage/ApplicationDetailsPage/ApplicationDetailsStep/hooks';
import {
  StyledParagraph,
  StyledSectionTitle,
  StyledStepTitle,
} from 'pages/MyApplicationsPage/ApplicationDetailsPage/ApplicationDetailsStep/WizardSteps/styles';
import AddPreviousAddressListItem from './AddPreviousAddressListItem';

const PersonalDataStep = () => {
  const [showPreviousAddresses, setShowPreviousAddresses] = useState<boolean>(false);
  const [showPreviousNames, setShowPreviousNames] = useState<boolean>(false);
  const [showWorkSection, setShowWorkSection] = useState<boolean>(false);
  const { unit, getProperties } = useGlobalProperty();
  const {
    loadDictionaries,
    civilianPrefix,
    gender,
    phoneCode,
    maritalStatus,
    identificationDocumentType,
  } = useApplicationDetails();

  useEffect(() => {
    if (!unit) {
      getProperties();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    control,
    watch,
    reset,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: initialPersonalDataStepFormValues,
  });
  const { fields } = useFieldArray({
    control,
    name: 'previousAddressesList',
  });

  const formRef = useRef<HTMLFormElement | null>(null);
  const watchPreviousAddresses = watch('previousAddresses');
  const watchChangedName = watch('changedName');
  const watchIsEmployment = watch('isEmployment');

  useEffect(() => {
    (async function initDictionaries() {
      await loadDictionaries([
        EDictionaryTypes.civilianPrefix,
        EDictionaryTypes.Gender,
        EDictionaryTypes.PhoneCode,
        EDictionaryTypes.MaritalStatus,
        EDictionaryTypes.IdentificationDocumentType,
      ]);
    })();
  }, []);

  useEffect(() => {
    const formData = ApplicationStore.getStepData('personalData');
    if (Object.keys(formData).length > 0) {
      reset(formData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setShowPreviousAddresses(watchPreviousAddresses === '1');
    if (watchPreviousAddresses === '0') {
      setValue('previousAddressesList', [
        {
          previousAddress: '',
          previousPostcode: '',
          prevCity: '',
          prevState: '',
          prevCountry: '',
          prevFromDate: '',
          prevToDate: '',
        },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchPreviousAddresses]);

  useEffect(() => {
    setShowPreviousNames(watchChangedName === '1');
  }, [watchChangedName]);

  useEffect(() => {
    setShowWorkSection(watchIsEmployment === '1');
  }, [watchIsEmployment]);

  return (
    <Box sx={{ marginTop: '24px' }}>
      <StyledStepTitle variant="h4">Personal Data</StyledStepTitle>
      <StyledParagraph variant="body2">
        Personal data (including sensitive personal data) will be processed on a public
        interest basis, for the purpose of allowing the police to assess the suitability of an
        applicant to be granted a firearm or shotgun certificate, or to assess the applicant’s
        continued suitability to possess a firearm or shotgun while the certificate remains
        valid.
      </StyledParagraph>

      <StyledSectionTitle variant="h5">Personal Information</StyledSectionTitle>

      <form ref={formRef}>
        <Grid container spacing={1}>
          <Grid item xs={4}>
            <FormSelect
              options={mapDictionaryToOptionProp(civilianPrefix)}
              setValue={setValue}
              name="title"
              label="Title"
              required
              control={control}
              errors={errors}
            />
          </Grid>
        </Grid>

        <Grid container spacing={1}>
          <Grid item xs={4}>
            <FormInput
              name="forename"
              label="First Name"
              required
              control={control}
              errors={errors}
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={4}>
            <FormInput
              name="middleNames"
              label="Middle Name(s)"
              control={control}
              errors={errors}
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={4}>
            <FormInput
              name="surname"
              label="Surname"
              required
              control={control}
              errors={errors}
              InputProps={{ readOnly: true }}
            />
          </Grid>
        </Grid>

        <Box>
          <FormRadioGroup
            name="changedName"
            label="Have you legally changed your name?"
            options={[
              { label: 'YES', value: '1' },
              { label: 'NO', value: '0' },
            ]}
            control={control}
            errors={errors}
          />
        </Box>

        {showPreviousNames && (
          <Grid container spacing={1}>
            <Grid item xs={4}>
              <FormInput
                name="previousSurname"
                label="Previous Surname"
                control={control}
                errors={errors}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={4}>
              <FormInput
                name="previousForenames"
                label="Previous Forenames"
                control={control}
                errors={errors}
                InputProps={{ readOnly: true }}
              />
            </Grid>
          </Grid>
        )}

        <Grid container spacing={1}>
          <Grid item xs={4}>
            <FormSelect
              options={mapDictionaryToOptionProp(gender)}
              setValue={setValue}
              name="gender"
              label="Gender"
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid item xs={4}>
            <FormSelect
              options={mapDictionaryToOptionProp(maritalStatus)}
              setValue={setValue}
              name="maritalStatus"
              label="Marital Status"
              control={control}
              errors={errors}
            />
          </Grid>
        </Grid>

        <StyledSectionTitle variant="h5">Identity Document</StyledSectionTitle>

        <Grid container spacing={1}>
          <Grid item xs={4}>
            <FormSelect
              options={mapDictionaryToOptionProp(identificationDocumentType)}
              setValue={setValue}
              name="documentType"
              label="Document Type"
              required
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid item xs={4}>
            <FormInput
              name="documentNumber"
              label="Document Number"
              required
              control={control}
              errors={errors}
              InputProps={{ readOnly: true }}
            />
          </Grid>
        </Grid>

        <StyledSectionTitle variant="h5">Your address</StyledSectionTitle>

        <Grid container spacing={1}>
          <Grid item xs={8}>
            <FormInput
              name="homeAddress"
              label="Home Address"
              required
              control={control}
              errors={errors}
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={4}>
            <FormInput
              name="postcode"
              label="Postcode"
              required
              control={control}
              errors={errors}
              InputProps={{ readOnly: true }}
            />
          </Grid>
        </Grid>

        <Grid container spacing={1}>
          <Grid item xs={4}>
            <FormInput
              name="city"
              label="City/Town"
              required
              control={control}
              errors={errors}
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={4}>
            <FormInput
              name="state"
              label="State/Province/County"
              required
              control={control}
              errors={errors}
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={4}>
            <FormInput
              name="country"
              label="Country"
              required
              control={control}
              errors={errors}
              InputProps={{ readOnly: true }}
            />
          </Grid>
        </Grid>

        <Grid container spacing={1}>
          <Grid item xs={8}>
            <FormInput
              name="typeOfResidence"
              label="Type of Residence"
              control={control}
              errors={errors}
              InputProps={{ readOnly: true }}
            />
          </Grid>
        </Grid>

        <Grid container spacing={1}>
          <Grid item xs={4}>
            <FormInput
              name="homePhoneNumber"
              label="Home Telephone Number"
              control={control}
              errors={errors}
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={4}>
            <FormSelect
              options={mapDictionaryToOptionProp(phoneCode)}
              setValue={setValue}
              name="mobilePrefix"
              label="Mobile Prefix"
              required
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid item xs={4}>
            <FormInput
              name="mobilePhoneNumber"
              label="Mobile Phone Number"
              required
              control={control}
              errors={errors}
              InputProps={{ readOnly: true }}
            />
          </Grid>
        </Grid>

        <Grid container spacing={1}>
          <Grid item xs={8}>
            <FormInput
              name="email"
              label="Email Address"
              control={control}
              errors={errors}
              InputProps={{ readOnly: true }}
            />
          </Grid>

          <Grid item xs={4}>
            <FormInput
              name="height"
              label="Height"
              control={control}
              errors={errors}
              InputProps={{ readOnly: true }}
              endAdornmentText="cm"
            />
          </Grid>
        </Grid>

        <Grid container spacing={1}>
          <Grid item xs={4}>
            <FormInput
              name="placeOfBirth"
              label="Place of Birth"
              control={control}
              errors={errors}
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={4}>
            <FormDatePicker
              name="dateOfBirth"
              openTo="year"
              label="Date of Birth*"
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid item xs={4}>
            <FormInput
              name="nationality"
              label="Nationality"
              required
              control={control}
              errors={errors}
              InputProps={{ readOnly: true }}
            />
          </Grid>
        </Grid>

        <StyledSectionTitle variant="h5">Previous Addresses</StyledSectionTitle>

        <Box>
          <FormRadioGroup
            name="previousAddresses"
            label="Have you moved your home address in the last 5 Years?"
            options={[
              { label: 'YES', value: '1' },
              { label: 'NO', value: '0' },
            ]}
            control={control}
            errors={errors}
          />
        </Box>

        {showPreviousAddresses && (
          <Box>
            {fields.map((field, index) => (
              <AddPreviousAddressListItem
                key={field.id}
                arrayName="previousAddressesList"
                index={index}
                control={control}
                errors={errors}
              />
            ))}
          </Box>
        )}

        <StyledSectionTitle variant="h5">Work Information</StyledSectionTitle>

        <Box>
          <FormRadioGroup
            name="isEmployment"
            label="Are you currently in employment?"
            options={[
              { label: 'YES', value: '1' },
              { label: 'NO', value: '0' },
            ]}
            control={control}
            errors={errors}
          />
        </Box>

        {showWorkSection && (
          <>
            <Grid container spacing={1}>
              <Grid item xs={8}>
                <FormInput
                  name="occupation"
                  label="Occupation"
                  control={control}
                  errors={errors}
                />
              </Grid>
            </Grid>

            <Grid container spacing={1}>
              <Grid item xs={8}>
                <FormInput
                  name="workAddress"
                  label="Work Address"
                  control={control}
                  errors={errors}
                />
              </Grid>
              <Grid item xs={4}>
                <FormInput
                  name="workPostcode"
                  label="Work Postcode"
                  control={control}
                  errors={errors}
                />
              </Grid>
            </Grid>

            <Grid container spacing={1}>
              <Grid item xs={4}>
                <FormInput
                  name="workCity"
                  label="City/Town"
                  required
                  control={control}
                  errors={errors}
                />
              </Grid>
              <Grid item xs={4}>
                <FormInput
                  name="workState"
                  label="State/Province"
                  required
                  control={control}
                  errors={errors}
                />
              </Grid>
              <Grid item xs={4}>
                <FormInput
                  name="workCountry"
                  label="Country"
                  required
                  control={control}
                  errors={errors}
                />
              </Grid>
            </Grid>

            <Grid container spacing={1}>
              <Grid item xs={4}>
                <FormInput
                  name="workPhoneNumber"
                  label="Work Telephone Number"
                  control={control}
                  errors={errors}
                />
              </Grid>
              <Grid item xs={8}>
                <FormInput
                  name="workEmail"
                  label="Work Email"
                  control={control}
                  errors={errors}
                />
              </Grid>
            </Grid>
          </>
        )}
      </form>
    </Box>
  );
};

export default PersonalDataStep;
